import {
  getSessionFind,
  sessionFinds,
  subscribeSession,
  type SessionFind,
} from "../shared/scan-session";
import { settings } from "../shared/settings";
import { scanChime } from "./chime";
import { make } from "./dom";
import { demoteFind, featuredFind, type FindCardModel } from "./find-card";
import { findPrice, printingLabel, type FindPrice, type FindSummary } from "./find-prices";
import { ImageSource } from "./image-source";

export type { FindSummary } from "./find-prices";

/** Capture persistence changes no identity, valuation, or action on an existing row. */
function sameDetails(before: SessionFind, after: SessionFind): boolean {
  return (
    before.name === after.name &&
    before.printing === after.printing &&
    before.candidates === after.candidates &&
    before.verdict === after.verdict &&
    before.resolvedBy === after.resolvedBy
  );
}

/** Keyed rows retain focus and scroll position during arrivals and human review. */
export function mountFindLog(
  root: HTMLElement,
  openReview: (id: string) => void,
): {
  count: () => number;
  summary: () => FindSummary;
  featuredHost: HTMLElement;
  list: HTMLElement;
} {
  root.classList.add("scan-finds");
  root.setAttribute("aria-label", "Session finds, newest first");
  const featuredHost = make("div", "featured-host");
  featuredHost.hidden = true;
  const list = make("div", "find-list");
  list.hidden = true;
  list.setAttribute("role", "region");
  list.setAttribute("aria-label", "Earlier session finds, newest first");
  list.tabIndex = 0;
  list.style.overflowAnchor = "none";
  const announcer = make("div", "sr-only");
  announcer.setAttribute("role", "status");
  announcer.setAttribute("aria-live", "polite");
  announcer.setAttribute("aria-atomic", "true");
  root.replaceChildren(featuredHost, list, announcer);

  const nodes = new Map<
    string,
    { button: HTMLButtonElement; row: SessionFind; price: FindPrice; image: ImageSource }
  >();
  let totals: FindSummary = { count: 0, unresolved: 0, values: [], unpriced: 0 };
  let arrivalTimer = 0;
  let arrivingId: string | undefined;
  let renderedRows: readonly SessionFind[] | undefined;

  const modelFor = (row: SessionFind, price: FindPrice, source: ImageSource): FindCardModel => {
    return {
      name: row.name,
      printingLabel: printingLabel(row.printing),
      ambiguous: row.verdict === "AMBIGUOUS",
      resolved: row.verdict === "RESOLVED",
      resolvedBy: row.verdict === "RESOLVED" ? (row.resolvedBy ?? "user") : undefined,
      prices: price.labels,
      priceNotes: price.notes,
      preview: () => {
        const url = source.get(row.preview);
        if (!url) return null;
        const image = make("img");
        image.src = url;
        image.alt = "";
        return image;
      },
      open: () => {
        if (getSessionFind(row.id)?.verdict === "AMBIGUOUS") openReview(row.id);
      },
    };
  };

  const render = (arrivalId?: string): void => {
    const rows = sessionFinds();
    if (rows === renderedRows) return;
    renderedRows = rows;
    const previousTop = list.scrollTop;
    const listTop = previousTop > 2 ? list.getBoundingClientRect().top : 0;
    const anchor =
      previousTop > 2
        ? [...list.children].find((node) => node.getBoundingClientRect().bottom > listTop)
        : undefined;
    const anchorTop = anchor?.getBoundingClientRect().top;
    const focused = document.activeElement;
    const heldFocus = focused instanceof HTMLElement && root.contains(focused);
    const liveIds = new Set(rows.map((row) => row.id));
    for (const [id, entry] of nodes) {
      if (!liveIds.has(id)) {
        entry.button.remove();
        entry.image.clear();
        nodes.delete(id);
      }
    }
    totals = { count: rows.length, unresolved: 0, values: [], unpriced: 0 };
    rows.forEach((row, index) => {
      let entry = nodes.get(row.id);
      const price =
        entry && sameDetails(entry.row, row)
          ? entry.price
          : findPrice(
              row,
              row.candidates.filter((candidate) => candidate.printing.name === row.name),
            );
      if (row.verdict === "AMBIGUOUS") totals.unresolved++;
      if (price.unpriced) totals.unpriced++;
      for (const value of price.values) {
        const total = totals.values.find((entry) => entry.currency === value.currency);
        if (total) {
          total.low += value.low;
          total.high += value.high;
        } else totals.values.push({ ...value });
      }
      if (!entry) {
        const image = new ImageSource();
        const button = featuredFind(modelFor(row, price, image));
        button.dataset.findId = row.id;
        entry = { button, row, price, image };
        nodes.set(row.id, entry);
      } else if (entry.row !== row) {
        const replacement = featuredFind(modelFor(row, price, entry.image));
        entry.button.replaceChildren(...replacement.childNodes);
        entry.button.setAttribute("aria-label", replacement.getAttribute("aria-label") ?? row.name);
        entry.button.classList.toggle("is-ambiguous", row.verdict === "AMBIGUOUS");
        entry.button.classList.toggle("is-resolved", row.verdict === "RESOLVED");
        if (row.verdict === "RESOLVED") entry.button.dataset.resolvedBy = row.resolvedBy ?? "user";
        else delete entry.button.dataset.resolvedBy;
        entry.row = row;
        entry.price = price;
      }
      const button = entry.button;
      // The accepted-row sheet is outside this handoff. Keep its content readable,
      // without opening the removed card-detail screen.
      const disabled = String(row.verdict !== "AMBIGUOUS");
      if (button.getAttribute("aria-disabled") !== disabled)
        button.setAttribute("aria-disabled", disabled);
      const tabIndex = row.verdict === "AMBIGUOUS" ? 0 : -1;
      if (button.tabIndex !== tabIndex) button.tabIndex = tabIndex;
      if (index === 0) {
        if (!button.classList.contains("find-featured")) {
          button.classList.remove("find-row", "is-older");
          button.classList.add("find-featured");
        }
        if (button.parentElement !== featuredHost) featuredHost.append(button);
        if (arrivalId === row.id) {
          button.style.removeProperty("animation");
          clearTimeout(arrivalTimer);
          arrivingId = row.id;
          arrivalTimer = window.setTimeout(() => {
            button.style.animation = "none";
            arrivingId = undefined;
          }, 220);
          const identity = row.verdict === "AMBIGUOUS" ? "ambiguous" : printingLabel(row.printing);
          announcer.textContent = `${row.name}, ${identity}. ${price.labels.join("; ")}.`;
          if (settings.chime) scanChime(price.minimum);
        } else if (arrivingId !== row.id && button.style.animation !== "none")
          button.style.animation = "none";
      } else {
        if (button.classList.contains("find-featured")) demoteFind(button);
        const position = list.children[index - 1];
        if (position !== button) list.insertBefore(button, position ?? null);
      }
    });
    if (featuredHost.hidden !== (rows.length === 0)) featuredHost.hidden = rows.length === 0;
    if (list.hidden !== rows.length < 2) list.hidden = rows.length < 2;
    if (root.dataset.count !== String(rows.length)) root.dataset.count = String(rows.length);
    if (previousTop > 2 && anchor?.isConnected && anchorTop !== undefined)
      list.scrollTop = previousTop + anchor.getBoundingClientRect().top - anchorTop;
    if (heldFocus && focused instanceof HTMLElement && document.activeElement !== focused) {
      const available =
        focused.isConnected &&
        !focused.closest("[hidden], [inert]") &&
        focused.getClientRects().length > 0;
      (available
        ? focused
        : (featuredHost.querySelector<HTMLButtonElement>("button") ?? list)
      ).focus({
        preventScroll: true,
      });
    }
  };

  subscribeSession((change) => {
    if (change.type === "update" && change.id) {
      const row = getSessionFind(change.id);
      const entry = nodes.get(change.id);
      if (row && entry && sameDetails(entry.row, row)) {
        // JPEG/thumbnail completion must not rebuild every find, replay the chime, or
        // interrupt the arrival animation. Only the capture surface needs a new image.
        if (row.preview !== entry.row.preview) {
          const surface = entry.button.querySelector(".capture");
          const image = modelFor(row, entry.price, entry.image).preview();
          if (image) image.className = "find-capture-image";
          surface?.replaceChildren(...(image ? [image] : []));
        }
        entry.row = row;
        renderedRows = sessionFinds();
        return;
      }
    }
    render(change.type === "add" ? change.id : undefined);
  });
  render();
  return {
    count: () => totals.count,
    summary: () => ({ ...totals, values: totals.values.map((value) => ({ ...value })) }),
    featuredHost,
    list,
  };
}
